import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { eraseDisc, eraseSegment, paintDisc, paintSegment } from '../../lib/inpaint/mask';

/**
 * Image viewer with a paintable mask overlay.
 *
 * The overlay lets the user extend or trim the removal region by hand, which is
 * what makes removal work for watermarks we have no template for — and what lets
 * them catch the companion star that sits beside the Gemini star (the template
 * only covers the main one).
 *
 * Two details are easy to get wrong and are handled explicitly here:
 *
 *  - **Letterboxing.** The preview uses `object-contain`, so the element's box
 *    can be wider than the rendered image content. Naively scaling pointer
 *    coordinates by `naturalWidth / rect.width` puts the brush in the wrong
 *    place whenever the image is letterboxed. We compute the true content box
 *    from the rendered rect and the aspect ratio instead.
 *
 *  - **Redraw cost.** Repainting a full mask `ImageData` on every pointermove
 *    stalls the main thread on large images. So the visible overlay is painted
 *    incrementally with canvas arc fills, while the authoritative `Uint8Array`
 *    is updated alongside. Undo/clear do redraw wholesale, which is fine
 *    because they are one-off clicks.
 */

const MASK_COLOR = 'rgba(239, 68, 68, 0.55)';
const ERASE_COLOR = 'rgba(59, 130, 246, 0.55)';

export interface WatermarkMaskEditorProps {
  imageSrc: string;
  imageWidth: number;
  imageHeight: number;
  mask: Uint8Array | null;
  onMaskChange: (mask: Uint8Array) => void;
  /** Brush radius in *image* pixels. */
  brushRadius: number;
  /** When true, strokes remove mask instead of adding it. */
  erasing: boolean;
  editable: boolean;
  onImageLoad?: (img: HTMLImageElement) => void;
  imgRef?: React.RefObject<HTMLImageElement | null>;
  className?: string;
}

interface ContentBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

const MAX_UNDO = 12;
/**
 * Each undo entry is a full mask snapshot (width*height bytes), so the count cap
 * alone allows ~290 MB on a 24 MP image. This caps total history size; at least
 * one step is always kept.
 */
const MAX_UNDO_BYTES = 64 * 1024 * 1024;

export function WatermarkMaskEditor({
  imageSrc,
  imageWidth,
  imageHeight,
  mask,
  onMaskChange,
  brushRadius,
  erasing,
  editable,
  onImageLoad,
  imgRef,
  className,
}: WatermarkMaskEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [contentBox, setContentBox] = useState<ContentBox | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  // Tracks the image the undo stack belongs to. When it changes, the stack and
  // Undo are reset — this happens during render so no cascading effect is needed.
  const [undoImageKey, setUndoImageKey] = useState(() => `${imageSrc}@${imageWidth}x${imageHeight}`);

  // Mutable working copy: `mask` is the committed state, this is what we
  // mutate during a stroke so we do not re-render on every pointermove.
  const maskRef = useRef<Uint8Array | null>(mask);
  const undoRef = useRef<Uint8Array[]>([]);
  const strokeRef = useRef<{ x: number; y: number } | null>(null);
  const [strokeActive, setStrokeActive] = useState(false);

  useEffect(() => {
    maskRef.current = mask;
  }, [mask]);

  // Snapshots belong to one image. Restoring one into a differently sized mask
  // throws (smaller) or leaves stale pixels (larger). Reset the key and disable
  // Undo during render when the image changes; the effect then only clears the
  // stack (a setState there would cascade a render).
  const currentImageKey = `${imageSrc}@${imageWidth}x${imageHeight}`;
  if (currentImageKey !== undoImageKey) {
    setUndoImageKey(currentImageKey);
    setCanUndo(false);
  }
  useEffect(() => {
    undoRef.current = [];
  }, [undoImageKey]);

  /** Repaint the whole overlay from the current mask. */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const current = maskRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!current) return;

    const image = ctx.createImageData(canvas.width, canvas.height);
    const data = image.data;
    for (let i = 0; i < current.length; i++) {
      if (!current[i]) continue;
      data[i * 4] = 239;
      data[i * 4 + 1] = 68;
      data[i * 4 + 2] = 68;
      data[i * 4 + 3] = 140;
    }
    ctx.putImageData(image, 0, 0);
  }, []);

  // Redraw whenever the committed mask changes from outside (auto-detection,
  // clear, undo).
  useEffect(() => {
    redraw();
  }, [mask, redraw]);

  /**
   * Position the overlay exactly over the rendered image content.
   *
   * The state update is skipped when the numbers have not actually moved. That
   * matters: this runs from a ResizeObserver, and any layout change elsewhere on
   * the page (such as the result panel appearing once an image is processed)
   * would otherwise re-render the editor, which re-runs layout, which notifies
   * the observer again — a feedback loop that pins the renderer at 100% CPU and
   * never lets the page respond again.
   */
  const syncContentBox = useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef?.current ?? wrap?.querySelector('img');
    if (!wrap || !img || imageWidth === 0 || imageHeight === 0) return;

    const wrapRect = wrap.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const scale = Math.min(imgRect.width / imageWidth, imgRect.height / imageHeight);
    if (!Number.isFinite(scale) || scale <= 0) return;

    const width = imageWidth * scale;
    const height = imageHeight * scale;
    const left = imgRect.left - wrapRect.left + (imgRect.width - width) / 2;
    const top = imgRect.top - wrapRect.top + (imgRect.height - height) / 2;

    // Sub-pixel jitter is not worth a re-render.
    const EPSILON = 0.5;
    setContentBox((prev) => {
      if (
        prev &&
        Math.abs(prev.left - left) < EPSILON &&
        Math.abs(prev.top - top) < EPSILON &&
        Math.abs(prev.width - width) < EPSILON &&
        Math.abs(prev.height - height) < EPSILON
      ) {
        return prev;
      }
      return { left, top, width, height };
    });
  }, [imgRef, imageWidth, imageHeight]);

  useLayoutEffect(() => {
    syncContentBox();
  }, [syncContentBox, imageSrc, imageWidth, imageHeight]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;

    let queued = false;
    const observer = new ResizeObserver(() => {
      // Coalesce bursts into one measurement per frame.
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        syncContentBox();
      });
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [syncContentBox]);

  const toImageCoords = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const img = imgRef?.current;
      if (!img) return null;
      const rect = img.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;

      const scale = Math.min(rect.width / imageWidth, rect.height / imageHeight);
      if (scale <= 0) return null;
      const contentWidth = imageWidth * scale;
      const contentHeight = imageHeight * scale;
      const originX = rect.left + (rect.width - contentWidth) / 2;
      const originY = rect.top + (rect.height - contentHeight) / 2;

      return {
        x: (clientX - originX) / scale,
        y: (clientY - originY) / scale,
      };
    },
    [imgRef, imageWidth, imageHeight]
  );

  const pushUndo = useCallback(() => {
    const current = maskRef.current;
    if (!current) return;
    const stack = undoRef.current;
    stack.push(current.slice());
    while (
      stack.length > 1 &&
      (stack.length > MAX_UNDO || stack.length * current.length > MAX_UNDO_BYTES)
    ) {
      stack.shift();
    }
    setCanUndo(true);
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!editable || !maskRef.current) return;
    const point = toImageCoords(e.clientX, e.clientY);
    if (!point) return;
    e.currentTarget.setPointerCapture(e.pointerId);

    pushUndo();

    const current = maskRef.current!;
    if (erasing) {
      eraseDisc(current, imageWidth, imageHeight, point.x, point.y, brushRadius);
    } else {
      paintDisc(current, imageWidth, imageHeight, point.x, point.y, brushRadius);
    }

    strokeRef.current = point;
    setStrokeActive(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!strokeRef.current || !editable) return;
    const point = toImageCoords(e.clientX, e.clientY);
    const last = strokeRef.current;
    if (!point || !last) return;

    const current = maskRef.current;
    if (!current) return;

    if (erasing) {
      eraseSegment(
        current,
        imageWidth,
        imageHeight,
        last.x,
        last.y,
        point.x,
        point.y,
        brushRadius
      );
    } else {
      paintSegment(
        current,
        imageWidth,
        imageHeight,
        last.x,
        last.y,
        point.x,
        point.y,
        brushRadius
      );
    }

    // Incremental overlay draw for this segment.
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = erasing ? ERASE_COLOR : MASK_COLOR;
      ctx.lineWidth = brushRadius * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }

    strokeRef.current = point;
  };

  const finishStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!strokeRef.current) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    strokeRef.current = null;
    setStrokeActive(false);

    if (maskRef.current) onMaskChange(maskRef.current);
  };

  useEffect(() => {
    if (!strokeActive && maskRef.current) redraw();
  }, [strokeActive, redraw]);

  const undo = useCallback(() => {
    const previous = undoRef.current.pop();
    if (!previous || !maskRef.current || previous.length !== maskRef.current.length) {
      setCanUndo(undoRef.current.length > 0);
      return;
    }
    maskRef.current.set(previous);
    setCanUndo(undoRef.current.length > 0);
    redraw();
    onMaskChange(maskRef.current);
  }, [onMaskChange, redraw]);

  const clearAll = useCallback(() => {
    const current = maskRef.current;
    if (!current) return;
    pushUndo();
    current.fill(0);
    redraw();
    onMaskChange(current);
  }, [pushUndo, onMaskChange, redraw]);

  const controlsEnabled = editable && !!mask;

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <img
        ref={imgRef}
        src={imageSrc}
        alt="Original"
        className="w-full max-h-[500px] object-contain rounded-lg shadow-sm"
        draggable={false}
        onLoad={(e) => {
          syncContentBox();
          onImageLoad?.(e.currentTarget);
        }}
      />

      {mask && contentBox && (
        <canvas
          ref={canvasRef}
          width={imageWidth}
          height={imageHeight}
          className={cn(
            'absolute rounded-lg touch-none',
            editable ? (strokeActive ? 'cursor-grabbing' : 'cursor-crosshair') : 'pointer-events-none'
          )}
          style={{
            left: contentBox.left,
            top: contentBox.top,
            width: contentBox.width,
            height: contentBox.height,
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
        />
      )}

      {controlsEnabled && (
        <div className="absolute right-2 top-2 flex gap-1">
          <button
            type="button"
            onClick={undo}
            disabled={!canUndo}
            className="rounded bg-white/90 px-2 py-1 text-[11px] font-semibold text-slate-700 shadow disabled:opacity-40 hover:bg-white cursor-pointer"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="rounded bg-white/90 px-2 py-1 text-[11px] font-semibold text-slate-700 shadow hover:bg-white cursor-pointer"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
